import React, { useContext, useEffect, useState, ChangeEvent } from 'react';
import { Grid, Box } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import AppsContext from './appContext';
import { applicationsService } from '@app/services';
import { AxiosResponse, AxiosError } from 'axios';
import { IgetCertificateRespose, ICertificate, ICertificateLocal, ISigningDialogState } from '@app/models';
import { Certificate, SigningDialog } from './signingEcryption';
import { PasslyButton } from '@app/components/Buttons';
import { Publish, Autorenew, Delete } from '@material-ui/icons';
import { genericErrorToast, generateCertificateFormData, atoBlob, theme } from '@app/helpers';
import { IModalData, ModalComponent } from '@app/components';
import { CERTIFICATE_TYPE } from '@app/pages/ssoManager/addEditApplication.enums';
import { setApplicationCertificate } from '@app/store/actions';
import { useDispatch } from 'react-redux';
import { red } from '@material-ui/core/colors';

export const AppSigningEncryption: React.SFC = () => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const app = useContext(AppsContext);

    const [certificates, setCertificates] = useState<{ signing: ICertificateLocal | null; encryption: ICertificateLocal | null }>({
        signing: null,
        encryption: null
    });

    const modalInitialState: IModalData = {
        localModalSwitcher: false,
        headerTitle: ''
    };

    const [modalData, setModalData] = useState<IModalData>(modalInitialState);

    useEffect(() => {
        loadCertificate(app.Id);
    }, []);

    const loadCertificate = (appId: string) => {
        applicationsService.getCertificate(appId, genericErrorToast, handleGetCertificate);
    };

    const upDateCertificate = (name: string, value: ICertificateLocal) => {
        setCertificates((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleGetCertificate = ({ data: { value } }: AxiosResponse<IgetCertificateRespose>) => {
        value?.forEach(function (key: ICertificate) {
            if (key.Type === CERTIFICATE_TYPE.signing) {
                upDateCertificate(CERTIFICATE_TYPE.signing.toLocaleLowerCase(), mapResponseCertToState(key));
                dispatch(setApplicationCertificate(key));
            }

            updateEncryption(key);
        });
    };

    const mapResponseCertToState = (responseState: ICertificate): ICertificateLocal => {
        const blob = atoBlob(responseState.RawData, 'data:application/octet-stream');
        const blobUrl = URL.createObjectURL(blob);

        return {
            type: responseState.Type,
            certificate: responseState,
            certUrl: blobUrl,
            certFileName: responseState.FileName,
            errors: { upload: '' }
        };
    };

    const handleEncryptionUpload = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
        applicationsService.uploadEncryptionCertificate(
            app.Id,
            generateCertificateFormData(files as FileList),
            genericErrorToast,
            onUploadEncryptionSuccess
        );
    };

    const onUploadEncryptionSuccess = ({ data }: { data: ICertificate }) => {
        updateEncryption(data);
    };

    const clearErrors = () => {
        if (certificates.signing) {
            setCertificates({ ...certificates, signing: { ...certificates.signing, errors: { upload: '' } } });
        }
    };

    const handleSubmit = ({ file, password, subjectName }: ISigningDialogState) => {
        clearErrors();
        file && password
            ? applicationsService.uploadSigningCertificate(app.Id, file, password.value, onUploadError, onUploadSuccess)
            : applicationsService.generateSigningCertificate(app.Id, subjectName, onUploadError, onUploadSuccess);
    };

    const handleClose = () => {
        setModalData((prevState) => ({
            ...prevState,
            localModalSwitcher: false
        }));
    };

    const onUploadError = ({ response }: AxiosError<{ error: { message: string } }>) => {
        if (certificates.signing) {
            setCertificates({ ...certificates, signing: { ...certificates.signing, errors: { upload: response?.data?.error?.message } } });
        }
    };

    const removeEncryptionCert = () => {
        applicationsService.removeEncryptionCertificate(app.Id, genericErrorToast, onRemoveEncryptionSuccess);
    };

    const onRemoveEncryptionSuccess = () => {
        setCertificates({ ...certificates, encryption: null });
    };

    const onUploadSuccess = ({ data }: { data: ICertificate }) => {
        updateSignin(data);
        handleClose();
    };

    const updateSignin = (data: ICertificate) => {
        if (data.Type === CERTIFICATE_TYPE.signing) {
            upDateCertificate(CERTIFICATE_TYPE.signing.toLowerCase(), mapResponseCertToState(data));
        }
    };

    const updateEncryption = (data: ICertificate) => {
        if (data.Type === CERTIFICATE_TYPE.encryption) {
            upDateCertificate(CERTIFICATE_TYPE.encryption.toLocaleLowerCase(), mapResponseCertToState(data));
        }
    };

    const uploadEncryptionCertButton = (label: string) => {
        return (
            <PasslyButton variant='contained' color='primary' component='label' startIcon={<Publish />}>
                <input
                    className='image-uploader__upload-input'
                    type='file'
                    name='encryptionUpload'
                    id='encryptionUpload'
                    accept='.cer'
                    onChange={handleEncryptionUpload}
                />
                {label}
            </PasslyButton>
        );
    };

    const generateNewSertificate = () => {
        setModalData({
            headerTitle: translation('GENERATE_NEW_CERTIFICATE'),
            localModalSwitcher: true,
            modalSwitcherCallback: handleClose,
            form: <SigningDialog onClose={handleClose} onSubmit={handleSubmit} errorMessage={certificates.signing?.errors?.upload} />
        });
    };

    return (
        <>
            <ModalComponent {...modalData} />
            <Grid container justifyContent='space-between' className='configpanel-row app-signing-encryption'>
                <Grid item xs={12} lg={7} className='configpanel-left'>
                    <Box p={2}>
                        {certificates?.signing ? (
                            <Certificate
                                certType={translation('SIGNING_CERTIFICATE')}
                                localCertificate={certificates.signing}
                                cardAditionalAction={
                                    <PasslyButton
                                        id='uploadCertificate'
                                        variant='contained'
                                        style={{ backgroundColor: red[400], color: theme.palette.primary.contrastText }}
                                        onClick={generateNewSertificate}
                                        startIcon={<Autorenew />}
                                    >
                                        {translation('GENERATE_NEW_CERTIFICATE')}
                                    </PasslyButton>
                                }
                            />
                        ) : (
                            <h3>{translation('APP_HAS_NO_SIGNING_CERT')}</h3>
                        )}
                        {certificates.encryption ? (
                            <Certificate
                                certType={translation('ENCRYPTION_CERTIFICATE')}
                                showKeys={['Subject', 'Issuer', 'Date']}
                                localCertificate={certificates.encryption}
                                cardAditionalAction={
                                    <Box>
                                        <PasslyButton
                                            id='uploadCertificate'
                                            variant='contained'
                                            onClick={() => removeEncryptionCert()}
                                            startIcon={<Delete />}
                                        >
                                            {translation('REMOVE_CERTIFICATE')}
                                        </PasslyButton>
                                        {uploadEncryptionCertButton(translation('UPLOAD_NEW_CERTIFICATE'))}
                                    </Box>
                                }
                            />
                        ) : (
                            <Box>{uploadEncryptionCertButton(translation('UPLOAD_NEW_ENCRYPTION_CERTIFICATE'))}</Box>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} lg={5} className='configpanel-right'>
                    <Box p={4}>
                        <div dangerouslySetInnerHTML={{ __html: translation('APP_CRYPTO_HELP') }} />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
